import React from 'react';
import { Box, Typography, CircularProgress, Autocomplete, TextField, Chip } from '@mui/material';

import type { Campaign } from '../types';

interface CampaignSelectorProps {
    campaigns?: Campaign[];
    selectedCampaigns: Campaign[];
    onSelectionChange: (campaigns: Campaign[]) => void;
    isLoading?: boolean;
}

/**
 * Renders a campaign selector using multiselect with autocomplete.
 * https://mui.com/material-ui/react-autocomplete/#multiple-values
 *
 * @param {CampaignSelectorProps} props
 * @returns {JSX.Element}
 */
const CampaignSelector: React.FC<CampaignSelectorProps> = (props) => {
    const { campaigns = [], selectedCampaigns, onSelectionChange, isLoading = false } = props;

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Search for the projects you want to copy questions to. <br />
                <strong>Note:</strong> This action will overwrite any existing questions in the selected projects.
            </Typography>
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : (
                <Autocomplete
                    multiple
                    id="copy-campaigns-autocomplete"
                    options={campaigns}
                    value={selectedCampaigns}
                    onChange={(event, newValue) => {
                        onSelectionChange(newValue);
                    }}
                    getOptionLabel={(option) => option.name || 'Unknown project'}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase();
                        const selectedIds = new Set(selectedCampaigns.map((c) => c.id));

                        return options.filter((option) => {
                            // Filter out already selected campaigns
                            if (selectedIds.has(option.id)) {
                                return false;
                            }

                            // If no search input, show all non-selected campaigns
                            if (!inputValue) {
                                return true;
                            }

                            // Filter by search input
                            return (
                                option.name?.toLowerCase().includes(inputValue) ||
                                option.desc?.toLowerCase().includes(inputValue) ||
                                option.office?.toLowerCase().includes(inputValue)
                            );
                        });
                    }}
                    renderInput={(params) => <TextField {...params} label="Select projects" placeholder="Search by name..." />}
                    renderValue={(value: Campaign[], getItemProps) =>
                        value.map((option: Campaign, index: number) => {
                            const { key, ...itemProps } = getItemProps({ index });
                            return <Chip label={option.name} key={key} {...itemProps} />;
                        })
                    }
                    renderOption={(optionProps, option) => (
                        <li {...optionProps} key={option.id} style={{ padding: '6px 16px' }}>
                            <Typography variant="body1">{option.name}</Typography>
                        </li>
                    )}
                    noOptionsText="No projects available"
                    slotProps={{
                        listbox: {
                            style: { maxHeight: '400px', padding: 0 },
                        },
                    }}
                />
            )}
        </Box>
    );
};

export default CampaignSelector;
